/**
 * AdminActivity - Activity timeline / audit log
 * Shows all events across the system with filtering
 */

import React, { useState, useEffect } from 'react';
import AdminShell from './AdminShell';
import { adminFetch } from './api';

const EVENT_COLORS = {
  login: '#3B82F6',
  logout: '#6B7280',
  prospect_viewed: '#FFB347',
  prospect_accepted: '#00D4AA',
  prospect_created: '#5B9DFF',
  client_created: '#10B981',
  client_converted: '#C084FC',
  ticket_created: '#F59E0B',
  ticket_updated: '#F59E0B',
  ticket_closed: '#10B981',
  invoice_created: '#5B9DFF',
  invoice_paid: '#00D4AA',
  document_uploaded: '#8B92A5',
  onboarding_step: '#3B82F6',
  onboarding_complete: '#10B981',
  service_activated: '#00D4AA',
  user_created: '#C084FC',
  settings_changed: '#F59E0B',
};

function formatDate(d) {
  if (!d) return '-';
  const date = new Date(d);
  const now = new Date();
  const diff = now - date;
  if (diff < 60000) return 'Just now';
  if (diff < 3600000) return `${Math.floor(diff / 60000)}m ago`;
  if (diff < 86400000) return `${Math.floor(diff / 3600000)}h ago`;
  if (diff < 604800000) return `${Math.floor(diff / 86400000)}d ago`;
  return date.toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
}

export default function AdminActivity() {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');

  useEffect(() => {
    adminFetch('/api/admin/activity?limit=100')
      .then(r => r.json())
      .then(d => { setEvents(d.events || []); setLoading(false); })
      .catch(() => {
        // API may not exist yet — show empty state
        setEvents([]);
        setLoading(false);
      });
  }, []);

  const categories = ['all', 'login', 'prospect', 'client', 'ticket', 'invoice', 'onboarding'];
  const filtered = filter === 'all' ? events : events.filter(e => (e.event_type || '').startsWith(filter));

  const s = {
    topBar: { display: 'flex', gap: 8, marginBottom: 24, flexWrap: 'wrap' },
    filterBtn: (active) => ({
      padding: '8px 16px', borderRadius: 20, border: '1px solid #2A3444', fontSize: 13,
      fontWeight: 500, cursor: 'pointer',
      backgroundColor: active ? '#1E293B' : 'transparent',
      color: active ? '#FFF' : '#8B92A5',
    }),
    card: { backgroundColor: '#1A2332', borderRadius: 12, border: '1px solid #2A3444', padding: 0 },
    event: {
      display: 'flex', alignItems: 'flex-start', gap: 14, padding: '16px 20px',
      borderBottom: '1px solid #2A344433',
    },
    dot: {
      width: 10, height: 10, borderRadius: '50%', marginTop: 5, flexShrink: 0,
    },
    actorBadge: (type) => ({
      padding: '2px 8px', borderRadius: 4, fontSize: 10, fontWeight: 700,
      textTransform: 'uppercase',
      backgroundColor: type === 'admin' ? 'rgba(59,130,246,0.12)' : type === 'client' ? 'rgba(16,185,129,0.12)' : 'rgba(192,132,252,0.12)',
      color: type === 'admin' ? '#3B82F6' : type === 'client' ? '#10B981' : '#C084FC',
    }),
  };

  return (
    <AdminShell title="Activity Timeline">
      <div style={s.topBar}>
        {categories.map(c => (
          <button key={c} style={s.filterBtn(filter === c)} onClick={() => setFilter(c)}>
            {c.charAt(0).toUpperCase() + c.slice(1)}
          </button>
        ))}
      </div>

      <div style={s.card}>
        {loading ? (
          <div style={{ padding: 40, textAlign: 'center', color: '#5A6175' }}>Loading activity...</div>
        ) : filtered.length === 0 ? (
          <div style={{ padding: 60, textAlign: 'center', color: '#5A6175' }}>
            {events.length === 0
              ? 'No activity recorded yet. Events will appear here as you and your team use the portal.'
              : 'No events match this filter.'}
          </div>
        ) : (
          filtered.map((evt, i) => {
            const color = EVENT_COLORS[evt.event_type] || '#5A6175';
            return (
              <div key={evt.id || i} style={s.event}>
                <div style={{ ...s.dot, backgroundColor: color }} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                    <span style={{ fontSize: 14, fontWeight: 600, color: '#FFF' }}>{evt.title || evt.event_type}</span>
                    <span style={s.actorBadge(evt.actor_type)}>{evt.actor_type}</span>
                  </div>
                  <div style={{ fontSize: 13, color: '#8B92A5' }}>
                    {evt.actor_name || 'System'} {evt.actor_email ? `(${evt.actor_email})` : ''}
                  </div>
                  {evt.description && (
                    <div style={{ fontSize: 12, color: '#5A6175', marginTop: 4 }}>{evt.description}</div>
                  )}
                  {evt.ip_address && (
                    <div style={{ fontSize: 11, color: '#3A4155', marginTop: 4 }}>IP: {evt.ip_address}</div>
                  )}
                </div>
                <div style={{ fontSize: 12, color: '#5A6175', whiteSpace: 'nowrap', flexShrink: 0 }}>
                  {formatDate(evt.created_at)}
                </div>
              </div>
            );
          })
        )}
      </div>
    </AdminShell>
  );
}
